import { URLS } from "@app/shared";
import footerBg from "@assets/images/footer-bg.png";
import { Link } from "@design-system/components";
import { footerSocialLinks } from "@design-system/data";
import { useInViewSSR } from "@design-system/hooks";
import { FooterLogoIcon } from "@design-system/icons";
import { Copyright } from "@design-system/layouts/CommonLayout/Copyright";
import { FooterAccordion } from "@design-system/layouts/CommonLayout/FooterAccordion";
import { FooterLinks } from "@design-system/layouts/CommonLayout/FooterLinks";
import { ScrollToTopButton } from "@design-system/layouts/CommonLayout/ScrollToTopButton";
import { trans } from "@mongez/localization";
import { motion } from "framer-motion";

export function Footer() {
  const { ref: footerRef, isInView } = useInViewSSR<HTMLDivElement>({
    once: true,
    margin: "-100px",
  });

  return (
    <footer
      ref={footerRef}
      className="relative overflow-hidden bg-marine text-white"
    >
      <motion.img
        src={footerBg}
        alt="footer-bg"
        className="absolute bottom-2 end-[25%] h-[23rem] lg:end-0 lg:top-[12.5%] lg:h-[70%]"
        initial={{ x: "140%" }}
        animate={isInView ? { x: 0 } : { x: "120%" }}
        transition={{ duration: 1.2 }}
      />
      <div className="container relative z-10">
        <motion.div
          initial={{ x: "-200%" }}
          animate={isInView ? { x: 0 } : { x: "-200%" }}
          transition={{ duration: 1.2 }}
          className="items-center justify-between max-lg:flex"
        >
          <div className="flex justify-between py-8 max-lg:flex-col max-lg:gap-7 lg:items-center">
            <div className="flex flex-col gap-7 uppercase lg:gap-4">
              <span className="text-sm lg:text-lg">
                {trans("anyEnquiries")}
              </span>
              <Link
                to={URLS.contactUs}
                className="text-2xl font-bold transition-all hover:text-secondary lg:text-[2.5rem]"
              >
                {trans("getInTouch")} {">"}
              </Link>
            </div>
            <div className="flex items-center gap-4">
              {footerSocialLinks.map((link, index) => {
                const { Icon, href } = link;

                return (
                  <a
                    key={index}
                    href={href}
                    target="_blank"
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-white transition-all duration-500 hover:border-bright-orange hover:bg-bright-orange max-lg:p-2 lg:h-12 lg:w-12"
                  >
                    <Icon />
                  </a>
                );
              })}
            </div>
          </div>
          <div className="lg:hidden">
            <ScrollToTopButton />
          </div>
        </motion.div>
        <motion.div
          initial={{ x: "200%" }}
          animate={isInView ? { x: 0 } : { x: "200%" }}
          transition={{ duration: 1.2 }}
          className="grid grid-cols-1 border-y border-white border-opacity-10 xl:grid-cols-12"
        >
          <div className="flex flex-col border-white border-opacity-10 py-6 max-xl:border-b lg:gap-12 xl:col-span-4 xl:border-e xl:py-14">
            <Link to={URLS.home}>
              <FooterLogoIcon className="max-lg:w-48" />
            </Link>
            <div className="flex flex-col items-start gap-4 font-light text-white text-opacity-70 xl:text-lg">
              <div className="flex flex-col gap-2">
                <p>{trans("footerLocation")}</p>
                <a
                  className="font-normal text-bright-orange underline"
                  href="https://www.google.com.sa/maps/place/Zamil+Industrial/@26.4014886,50.136945,15z/data=!4m5!3m4!1s0x0:0xeba119cf80525bef!8m2!3d26.3964779!4d50.1433665?hl=en&shorturl=1"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {trans("getDirections")}
                </a>
              </div>
              <a
                dir="ltr"
                href="tel:+966 13 810 8111"
                className="text-lg text-white text-opacity-70"
              >
                +966 13 810 8111
              </a>
              <a href="mailto:info@senaat.com">info@senaat.com</a>
            </div>
          </div>
          <FooterLinks />
          <FooterAccordion />
        </motion.div>
        <Copyright />
      </div>
    </footer>
  );
}
